import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { api, LanguageOption } from "../api";

interface Props {
  /** The user's owned language codes — order matters for tab order. */
  languages: string[];
  /** The currently-displayed language code. */
  value: string;
  /** Active (tutor) language — shown with a subtle ★ marker. */
  active?: string;
  onChange: (code: string) => void;
  /**
   * Optional explicit "make this the tutor's language" action.
   * When provided, a quiet "Make active →" pill appears next to the
   * tabs whenever the viewed language differs from the active one.
   * The tabs themselves are view-only — they only call `onChange`.
   */
  onMakeActive?: (code: string) => void | Promise<void>;
}

/**
 * Inline language tabs for dashboard pages (Journey, Vocabulary).
 *
 * Mental model — kept consistent across the app:
 *   • Tabs change the VIEW only.
 *   • The sidebar dropdown (or the "Make active" pill below) changes
 *     the TUTOR's language.
 *
 * Hidden when the user owns only one language.
 */
export default function LanguageTabs({
  languages,
  value,
  active,
  onChange,
  onMakeActive,
}: Props) {
  const [catalog, setCatalog] = useState<LanguageOption[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getLanguages().then((r) => setCatalog(r.languages || [])).catch(() => {});
  }, []);

  if (!languages || languages.length <= 1) return null;

  function nameFor(code: string): string {
    return catalog.find((l) => l.code === code)?.name ?? code;
  }

  const viewingNonActive = !!active && value !== active;
  const activeName = active ? nameFor(active) : "";
  const viewName = nameFor(value);

  async function handleMakeActive() {
    if (!onMakeActive || busy) return;
    setBusy(true);
    try {
      await onMakeActive(value);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-6">
      <div className="flex flex-wrap items-center gap-2">
        {languages.map((code) => {
          const isSelected = code === value;
          const isActive = code === active;
          return (
            <button
              key={code}
              type="button"
              onClick={() => onChange(code)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${
                isSelected
                  ? "border-terracotta bg-terracotta/10 text-ink font-medium"
                  : "border-[rgba(42,36,26,0.15)] text-ink-soft hover:border-terracotta"
              }`}
            >
              {isActive && (
                <span className="text-terracotta" aria-label="active language">
                  ★
                </span>
              )}
              <span>{nameFor(code)}</span>
            </button>
          );
        })}

        {viewingNonActive && onMakeActive && (
          <button
            type="button"
            onClick={handleMakeActive}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-full border border-terracotta/40 bg-paper px-3 py-1 font-mono text-[11px] tracking-wide text-terracotta hover:bg-terracotta hover:text-paper transition-colors disabled:opacity-60"
          >
            {busy ? "switching…" : (
              <>
                Make {viewName} active <ArrowRight size={11} />
              </>
            )}
          </button>
        )}
      </div>

      {viewingNonActive && (
        <p className="mt-2 font-serif italic text-ink-faint text-[13px]">
          viewing {viewName} — your tutor speaks{" "}
          <span className="text-ink-soft">{activeName}</span>.
        </p>
      )}
    </div>
  );
}
